import {PaginaLogada} from "../../componentes";
import {useEffect, useMemo, useState} from "react";

import {exibirMensagemSucesso, HttpVerbo, xfetch} from "../../util";
import {useNavigate} from "react-router-dom";
import {ActionIcon, Badge, Box, Center, Grid, Group, Modal, TagsInput, TextInput, Tooltip} from '@mantine/core';
import CardPadrao from '../../componentes/card/card-padrao';
import {MantineReactTable, MRT_ColumnDef, useMantineReactTable} from 'mantine-react-table';
import {COL_RESP_4, COL_RESP_6, COR} from '../../config/constantes/constantes-mantine';
import {UsuarioDto} from "../../tipos/dto/usuario-dto";
import {MRT_Localization_PT_BR} from "mantine-react-table/locales/pt-BR";
import {IconPencil, IconTag, IconUserPlus} from "@tabler/icons-react";
import {getPermissao} from "../../config/permissoes";
import {BotaoFechar, BotaoMantine, BotaoSalvar} from "../../componentes/botao/botao-mantine";
import {useDisclosure} from "@mantine/hooks";
import {useForm} from "@mantine/form";
import SetorSelect from "../../componentes/form/setor-select/setorSelect";
import {UsuarioServico} from "../../servicos";
import {UsuarioCmd} from "../../tipos/cmd/usuario-cmd";
import {Usuario} from "../../tipos/usuario";


export default function Usuarios() {

  const navegacao = useNavigate()
  const [isCarregando, setIsCarregando] = useState(false)
  const [setor, setSetor] = useState(-1)
  const [permissoes, setPermissoes] = useState<string[]>([])
  const [modal, {open: openModal, close: closeModal}] = useDisclosure(false);
  const [usuarios, setUsuarios] = useState<UsuarioDto[]>([])

  const form = useForm<UsuarioCmd>({
    initialValues: {
      nome: "",
      usuario: "",
      email: "",
      setor: -1,
    },
    validate: {
      nome: (value) => (value.length <= 6 ? "Nome muito curto" : null),
      usuario: (value) => (value.length <= 4 ? "Nome muito curto" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Email inválido"),
    },
    // transformValues: (values) => ({
    //   email: `${values.email === null ? "" : values.email}`,
    // }),
  });

  const fetchPermissoes = async () => {
    setIsCarregando(true)
    const res = await xfetch('/permissao', {}, HttpVerbo.GET).finally(() => setIsCarregando(false));
    const json = await res.json();
    setPermissoes(json)
    setIsCarregando(false)
  }

  function buscarUsuarios() {
    setIsCarregando(true)
    xfetch('/usuario', {}, HttpVerbo.GET)
      .then(res => res.json())
      .then(json => {
        setUsuarios(json)
        setIsCarregando(false)
      })
  }

  useEffect(() => {
    buscarUsuarios()
    fetchPermissoes()
  }, [])

  function editarUsuario(usuario: UsuarioDto) {
    navegacao('/usuario/' + usuario.id);
  }

  function salvarUsuario() {
    setIsCarregando(true)
    UsuarioServico.cadastrarUsuario(form.getValues())
      .then(res => res?.json()
        .then((dados: Usuario) => {
          exibirMensagemSucesso(`Usuário ${dados.nome} cadastrado com sucesso`)
    })).finally(() => setIsCarregando(false))
  }

  const colunas = useMemo<MRT_ColumnDef<UsuarioDto>[]>(() => [
      {
        accessorKey: 'nome',
        header: 'Nome',
        filterVariant: 'autocomplete',
      },
      {
        accessorKey: 'email',
        header: 'E-mail',
        filterVariant: 'autocomplete',
      },
      {
        accessorKey: 'setor.nome',
        header: 'Setor',
        Cell: ({renderedCellValue, row}) => {
          if (renderedCellValue !== null) {
            return (
              <Tooltip label={row.original.setor?.sigla}>
                <Badge color={row.original.setor?.cor}>
                  {renderedCellValue}
                </Badge>
              </Tooltip>
            )
          }
          return ""
        },
        filterVariant: 'multi-select',
      },
      {
        id: 'role',
        header: 'Permissões',
        // filterVariant: 'multi-select',
        accessorFn: (row) => {
          return row.permissoes?.map((p, k) => {
            return <Badge key={k} variant={"light"} color={getPermissao(p.role!!)?.cor} m={5}>
              {p.role}
            </Badge>
          })
        },
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns: colunas,
    data: usuarios,
    autoResetPageIndex: false,
    enableSorting: false,
    localization: MRT_Localization_PT_BR,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: {
        pageIndex: 0,
        pageSize: 50
      },
      columnVisibility: {
        id: false
      }
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({row}) => (
      <Group wrap="nowrap" gap="lg">
        <Tooltip label="Alterar">
          <ActionIcon
            aria-label="Alterar"
            color={COR.ALERTA}
            onClick={() => {
              editarUsuario(row.original)
            }}>
            <IconPencil/>
          </ActionIcon>
        </Tooltip>
      </Group>
    ),
    renderTopToolbarCustomActions: ({table}) => (
      <Box
        style={{
          display: 'flex',
          gap: '16px',
          padding: '8px',
          flexWrap: 'wrap',
        }}
      >
        <BotaoMantine disabled color={"blue"} icone={IconUserPlus} onClick={openModal}>Novo Usuário</BotaoMantine>
      </Box>
    ),
  })

  return (
    <PaginaLogada titulo="Usuários" isCarregando={isCarregando}>
      <CardPadrao>
        <MantineReactTable table={table}/>
      </CardPadrao>

      <Modal opened={modal} onClose={closeModal} size={"xl"} title={"Novo Usuário"}>
        <form onSubmit={form.onSubmit(salvarUsuario)}>
          <Grid>
            <Grid.Col span={COL_RESP_4}>
              <TextInput
                required
                label={"Nome"}
                {...form.getInputProps('nome')}
                onChange={(e) => form.setFieldValue("nome", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_4}>
              <TextInput
                required
                label={"Email"}
                {...form.getInputProps('email')}
                onChange={(e) => form.setFieldValue("email", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_4}>
              <TextInput
                required
                label={"Nome de Usuário"}
                {...form.getInputProps('usuario')}
                onChange={(e) => form.setFieldValue("usuario", e.currentTarget.value)}
              />
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <SetorSelect label={"Setor"} setorId={setor} setSetorId={setSetor}/>
            </Grid.Col>
            <Grid.Col span={COL_RESP_6}>
              <TagsInput
                clearable
                label={"Permissões"}
                data={permissoes}
                leftSection={<IconTag/>}
                {...form.getInputProps('roles')}
                // value={permissoesUsuario.map(p => p.role !== undefined ? p.role.toString() : "")}
                onChange={(e) => form.setFieldValue('roles', e)}
                styles={{
                  pill: {backgroundColor: '#FFD43B'}
                }}
              />
            </Grid.Col>
            <Grid.Col span={12} mt={20}>
              <Center>
                <Group>
                  <BotaoFechar onClick={closeModal}/>
                  <BotaoSalvar type={"submit"}/>
                </Group>
              </Center>
            </Grid.Col>
          </Grid>
        </form>
      </Modal>

    </PaginaLogada>
  );
}